import NetInfo from '@react-native-community/netinfo';
import { timeoutLogger, TimeoutErrorCode, TimeoutSeverity } from './timeoutLogger';
import { createTracer } from './requestTracer';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  timeoutMs: number;
  retryableErrors: string[];
}

export type NetworkQuality = 'excellent' | 'good' | 'poor' | 'offline';

interface NetworkQualityInfo {
  quality: NetworkQuality;
  timeoutMs: number;
  shouldRetry: boolean;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  timeoutMs: 10000,
  retryableErrors: ['PGRST301', 'AbortError', 'Aborted', 'NetworkError', 'TimeoutError'],
};

const NETWORK_QUALITY_THRESHOLDS = {
  excellent: { minSpeed: 5000, timeout: 10000 },
  good: { minSpeed: 1000, timeout: 15000 },
  poor: { minSpeed: 0, timeout: 20000 },
};

class NetworkRetryService {
  private currentQuality: NetworkQuality = 'excellent';
  private retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG;

  async initialize(): Promise<void> {
    await this.updateNetworkQuality();

    NetInfo.addEventListener((state) => {
      this.updateNetworkQuality();
    });
  }

  private async updateNetworkQuality(): Promise<void> {
    try {
      const state = await NetInfo.fetch();

      if (!state.isConnected) {
        this.currentQuality = 'offline';
        console.log('🌐 [NETWORK-RETRY] Network quality: OFFLINE');
        return;
      }

      if (state.type === 'wifi') {
        this.currentQuality = 'excellent';
      } else if (state.type === 'cellular') {
        const effectiveType = (state.details as any)?.cellularGeneration;

        if (effectiveType === '5g' || effectiveType === '4g') {
          this.currentQuality = 'good';
        } else if (effectiveType === '3g') {
          this.currentQuality = 'poor';
        } else {
          this.currentQuality = 'poor';
        }
      } else {
        this.currentQuality = 'good';
      }

      console.log(`🌐 [NETWORK-RETRY] Network quality updated: ${this.currentQuality.toUpperCase()}`);
    } catch (error) {
      console.error('🌐 [NETWORK-RETRY] Error detecting network quality:', error);
      this.currentQuality = 'good';
    }
  }

  getNetworkQualityInfo(): NetworkQualityInfo {
    const qualityMap: Record<NetworkQuality, NetworkQualityInfo> = {
      excellent: {
        quality: 'excellent',
        timeoutMs: NETWORK_QUALITY_THRESHOLDS.excellent.timeout,
        shouldRetry: true,
      },
      good: {
        quality: 'good',
        timeoutMs: NETWORK_QUALITY_THRESHOLDS.good.timeout,
        shouldRetry: true,
      },
      poor: {
        quality: 'poor',
        timeoutMs: NETWORK_QUALITY_THRESHOLDS.poor.timeout,
        shouldRetry: true,
      },
      offline: {
        quality: 'offline',
        timeoutMs: 5000,
        shouldRetry: false,
      },
    };

    return qualityMap[this.currentQuality];
  }

  getCurrentQuality(): NetworkQuality {
    return this.currentQuality;
  }

  private isRetryableError(error: any): boolean {
    if (!error) return false;

    const errorString = error.toString();
    const errorCode = error.code || error.name || '';
    const errorMessage = error.message || '';

    return this.retryConfig.retryableErrors.some(
      (retryableError) =>
        errorString.includes(retryableError) ||
        errorCode.includes(retryableError) ||
        errorMessage.includes(retryableError)
    );
  }

  private calculateBackoffDelay(attemptNumber: number): number {
    const exponentialDelay = this.retryConfig.baseDelay * Math.pow(2, attemptNumber);
    const jitter = Math.random() * 1000;
    return Math.min(exponentialDelay + jitter, this.retryConfig.maxDelay);
  }

  async withRetry<T>(
    operation: () => Promise<T>,
    operationName: string,
    customConfig?: Partial<RetryConfig>
  ): Promise<T> {
    const config = { ...this.retryConfig, ...customConfig };
    const networkInfo = this.getNetworkQualityInfo();
    const tracer = createTracer(`Network Retry: ${operationName}`, networkInfo.timeoutMs);

    tracer.addCheckpoint('Network quality check', {
      quality: networkInfo.quality,
      timeoutMs: networkInfo.timeoutMs,
      maxRetries: config.maxRetries,
    });

    if (networkInfo.quality === 'offline') {
      tracer.complete({ result: 'offline', error: 'No network connection' });
      throw new Error('OFFLINE: No network connection available');
    }

    let lastError: any;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          const delay = this.calculateBackoffDelay(attempt - 1);
          tracer.addCheckpoint(`Retry ${attempt} after backoff`, { delayMs: delay });
          console.log(
            `🔄 [NETWORK-RETRY] [${tracer.getRequestId()}] Retry attempt ${attempt}/${config.maxRetries} for "${operationName}" after ${delay}ms`
          );
          await this.sleep(delay);
        } else {
          console.log(`🌐 [NETWORK-RETRY] [${tracer.getRequestId()}] Attempting "${operationName}" (quality: ${networkInfo.quality})`);
        }

        tracer.addCheckpoint(`Attempt ${attempt + 1} started`);

        const result = await this.withTimeout(operation(), networkInfo.timeoutMs, operationName, tracer.getRequestId());

        tracer.addCheckpoint(`Attempt ${attempt + 1} succeeded`);

        if (attempt > 0) {
          console.log(`✅ [NETWORK-RETRY] [${tracer.getRequestId()}] "${operationName}" succeeded after ${attempt} retries`);
        }

        tracer.complete({ attempts: attempt + 1, result: 'success' });
        return result;
      } catch (error: any) {
        lastError = error;

        const isTimeout = error?.message?.includes('timeout') || error?.message?.includes('Timeout');
        const isAborted = error?.message?.includes('abort') || error?.message?.includes('Aborted');
        const isRetryable = this.isRetryableError(error);

        tracer.addCheckpoint(`Attempt ${attempt + 1} failed`, {
          errorType: error?.name,
          errorMessage: error?.message,
          isTimeout,
          isAborted,
          isRetryable,
        });

        console.log(
          `⚠️ [NETWORK-RETRY] [${tracer.getRequestId()}] "${operationName}" failed (attempt ${attempt + 1}/${config.maxRetries + 1}):`,
          {
            error: error?.message || error?.toString(),
            isTimeout,
            isAborted,
            isRetryable,
          }
        );

        const shouldRetry = (isRetryable || isTimeout || isAborted) && attempt < config.maxRetries;

        if (!shouldRetry) {
          tracer.addCheckpoint('No more retries available');

          if (isTimeout) {
            await timeoutLogger.logTimeout({
              requestId: tracer.getRequestId(),
              operationName: `Network Retry: ${operationName}`,
              timeoutDuration: networkInfo.timeoutMs,
              actualDuration: tracer.getDuration(),
              errorCode: TimeoutErrorCode.NETWORK_RETRY,
              severity: TimeoutSeverity.HIGH,
              retryAttempt: attempt + 1,
              metadata: {
                networkQuality: networkInfo.quality,
                maxRetries: config.maxRetries,
                totalAttempts: attempt + 1,
                errorMessage: error?.message,
              },
            });
          }

          break;
        }
      }
    }

    console.error(
      `❌ [NETWORK-RETRY] [${tracer.getRequestId()}] "${operationName}" failed after ${config.maxRetries + 1} attempts`
    );
    tracer.complete({ attempts: config.maxRetries + 1, result: 'failed', error: lastError?.message });
    throw lastError;
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, operationName: string, requestId?: string): Promise<T> {
    let timeoutId: NodeJS.Timeout;
    const startTime = Date.now();

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        const actualDuration = Date.now() - startTime;
        const reqId = requestId || 'UNKNOWN';
        console.error(`⏱️ [TIMEOUT] [${reqId}] "${operationName}" exceeded timeout of ${timeoutMs}ms (actual: ${actualDuration}ms)`);
        reject(new Error(`Timeout: "${operationName}" exceeded ${timeoutMs}ms`));
      }, timeoutMs);
    });

    try {
      const result = await Promise.race([promise, timeoutPromise]);
      clearTimeout(timeoutId!);
      const duration = Date.now() - startTime;
      const reqId = requestId || 'UNKNOWN';
      console.log(`✅ [TIMEOUT-CHECK] [${reqId}] "${operationName}" completed in ${duration}ms (limit: ${timeoutMs}ms)`);
      return result;
    } catch (error) {
      clearTimeout(timeoutId!);
      throw error;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async isOnline(): Promise<boolean> {
    try {
      const state = await NetInfo.fetch();
      return state.isConnected ?? false;
    } catch (error) {
      console.error('🌐 [NETWORK-RETRY] Error checking online status:', error);
      return false;
    }
  }
}

export const networkRetryService = new NetworkRetryService();
